import { jsx } from "panel";
import { SegmentFilterEl } from "common/charts/misc/segment-filter";
import { InsightsLineChartEl } from "common/report/insights/charts/insights-line-chart";
import { InsightsPieChartEl } from "common/report/insights/charts/insights-pie-chart";
import { InsightsMetricChartEl } from "common/report/insights/charts/insights-metric-chart";
import { InsightsBarChartEl } from "common/report/insights/charts/insights-bar-chart";
import { InsightsTableChartEl } from "common/report/insights/charts/insights-table-chart";
import { FunnelsStepChartEl } from "common/report/funnels/charts/funnels-step-chart";
import { FunnelStepBookmarkType } from "common/report/funnels/charts/funnels-step-chart/types";
import { InsightsTopPathsChartEl } from "common/report/insights/charts/insights-top-path-chart";
import { ChartType } from "common/report/constants";
import { RetentionCurveChartEl } from "common/report/retention/charts/retention-curve-chart";
import { RetentionBookmarkType } from "common/report/retention/charts/types";
export function template() {
  const { report } = this.state;
  const bookmarkParams = report.toBookmarkData();
  const chartRenderFn = getChartRenderFn(report.displayOptions.chartType);
  return /* @__PURE__ */ jsx("div", {
    sel: ".chart",
    class: {
      "resizing-layout": this.attr(`resizing-layout`),
      "download-mode": this.helpers.isDownloadMode()
    }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".main-chart"
  }, chartRenderFn(this, bookmarkParams)));
}
function getChartRenderFn(chartType) {
  switch (chartType) {
    case ChartType.Bar:
      return getBarChart;
    case ChartType.Line:
      return getLineChart;
    case ChartType.Pie:
      return getPieChart;
    case ChartType.Table:
      return getTableChart;
    case ChartType.Metric:
      return getMetricChart;
    case ChartType.FunnelSteps:
      return getStepChart;
    case ChartType.FunnelTopPaths:
      return getTopPaths;
    case ChartType.RetentionCurve:
      return getRetentionCurveChart;
    default:
      return null;
  }
}
function getLineChart(chartDisplay, bookmarkParams) {
  const downloadDisplayPreferences = chartDisplay.helpers.getDownloadDisplayPreferences();
  return /* @__PURE__ */ jsx(null, null, /* @__PURE__ */ jsx("div", {
    sel: ".common-line-container"
  }, /* @__PURE__ */ jsx("div", {
    sel: ".common-line-chart-container",
    class: { "fixed-size-chart": chartDisplay.getContext(`reportLayoutManager`).hasFixedSizeChart() }
  }, /* @__PURE__ */ jsx("div", {
    sel: ".chart-area"
  }, /* @__PURE__ */ jsx(InsightsLineChartEl, {
    sel: ".line-chart",
    attrs: {
      loaded: true,
      "show-annotations": !chartDisplay.helpers.isDownloadMode(),
      elref: `insightsLineChart`
    },
    props: {
      paramsAndResults: {
        params: bookmarkParams,
        results: chartDisplay.queryResults
      },
      urlLegendData: chartDisplay.state.report.legend.getDeprecatedUrlData(),
      popoverCallback: chartDisplay.helpers.lineChartPopoverCallback
    },
    on: {
      popoverAction: chartDisplay.helpers.handleLineChartPopoverAction
    }
  })))), downloadDisplayPreferences.isDownloadTruncated && /* @__PURE__ */ jsx("div", {
    sel: ".truncated-data-download-text"
  }, "Results have been truncated to the first 12 results for export."));
}
function getPieChart(chartDisplay, bookmarkParams) {
  return /* @__PURE__ */ jsx("div", {
    sel: ".common-pie-container",
    class: { "fixed-size-chart": chartDisplay.getContext(`reportLayoutManager`).hasFixedSizeChart() }
  }, /* @__PURE__ */ jsx(InsightsPieChartEl, {
    sel: ".pie-chart",
    attrs: {
      loaded: true
    },
    props: {
      paramsAndResults: {
        params: bookmarkParams,
        results: chartDisplay.queryResults
      },
      urlLegendData: chartDisplay.state.report.legend.getDeprecatedUrlData(),
      popoverCallback: chartDisplay.segmentPopoverCallback
    },
    on: {
      popoverAction: (ev) => chartDisplay.handleSegmentPopoverAction(ev.detail)
    }
  }));
}
function getTableChart(chartDisplay, bookmarkParams) {
  return /* @__PURE__ */ jsx(InsightsTableChartEl, {
    params: {
      bookmarkParams,
      results: chartDisplay.queryResults,
      resizingLayout: true,
      enablePivotView: chartDisplay.attr(`enable-pivot-view`),
      canUseViewN: chartDisplay.attr(`can-use-view-n`),
      downloadMode: chartDisplay.helpers.isDownloadMode(),
      popoverCallback: chartDisplay.segmentPopoverCallback,
      popoverActionCallback: chartDisplay.handleSegmentPopoverAction,
      sortChangeCallback: chartDisplay.helpers.dataTableSortChange,
      columnWidthOverrides: chartDisplay.tableChartColumnWidthOverrides,
      columnResizeCallback: chartDisplay.helpers.dataTableColumnResize
    }
  });
}
function getMetricChart(chartDisplay, bookmarkParams) {
  return /* @__PURE__ */ jsx("div", {
    sel: ".metric-container"
  }, /* @__PURE__ */ jsx(InsightsMetricChartEl, {
    props: {
      paramsAndResults: {
        params: bookmarkParams,
        results: chartDisplay.queryResults
      },
      urlLegendData: chartDisplay.state.report.legend.getDeprecatedUrlData(),
      popoverCallback: chartDisplay.helpers.metricChartPopoverCallback
    },
    on: {
      popoverAction: (ev) => chartDisplay.handleSegmentPopoverAction(ev.detail)
    },
    class: {
      "fixed-size-chart": chartDisplay.getContext(`reportLayoutManager`).hasFixedSizeChart()
    }
  }));
}
function getBarChart(chartDisplay, bookmarkParams) {
  return /* @__PURE__ */ jsx("div", {
    sel: ".common-bar-chart-container"
  }, /* @__PURE__ */ jsx(InsightsBarChartEl, {
    attrs: {
      "enable-sort": true,
      "download-mode": chartDisplay.helpers.isDownloadMode(),
      elref: `barChart`
    },
    props: {
      paramsAndResults: {
        params: bookmarkParams,
        results: chartDisplay.queryResults
      },
      barPopoverCallback: chartDisplay.segmentPopoverCallback,
      legendUrlData: chartDisplay.state.report.legend.getDeprecatedUrlData()
    },
    on: {
      columnWidthChange: (ev) => chartDisplay.helpers.updateBarColumnWidths(ev.detail.columnWidths),
      sortingChange: (ev) => chartDisplay.helpers.updateBarSortConfig(ev.detail.sortConfig),
      barPopoverAction: (ev) => chartDisplay.handleSegmentPopoverAction(ev.detail)
    }
  }), !chartDisplay.helpers.isDownloadMode() && /* @__PURE__ */ jsx(SegmentFilterEl, {
    sel: ".segment-filter",
    on: {
      "export-csv": chartDisplay.helpers.handleExportCsv
    }
  }));
}
function getStepChart(chartDisplay, bookmarkParams) {
  return /* @__PURE__ */ jsx("div", {
    sel: ".step-chart-container",
    class: {
      "fixed-size-chart": chartDisplay.getContext(`reportLayoutManager`).hasFixedSizeChart()
    }
  }, /* @__PURE__ */ jsx(FunnelsStepChartEl, {
    sel: ".funnels-step-chart",
    attrs: {
      loaded: true,
      "enable-segment-click": true,
      "min-step-width": 266,
      "max-step-width": 600,
      "resizing-layout": chartDisplay.attr(`resizing-layout`),
      "show-popover": true
    },
    class: {
      "resizing-layout": chartDisplay.attr(`resizing-layout`)
    },
    props: {
      bookmarkData: {
        queryResults: chartDisplay.queryResults,
        bookmark: bookmarkParams,
        type: FunnelStepBookmarkType.MULTIMETRIC
      },
      popoverCallback: chartDisplay.helpers.stepsChartPopoverCallback
    },
    on: {
      changeControlGroup: chartDisplay.helpers.handleChangeControlGroup
    }
  }));
}
function getTopPaths(chartDisplay, bookmarkParams) {
  return /* @__PURE__ */ jsx("div", {
    sel: ".flows-chart-container",
    class: {
      "fixed-size-chart": chartDisplay.getContext(`reportLayoutManager`).hasFixedSizeChart()
    }
  }, /* @__PURE__ */ jsx(InsightsTopPathsChartEl, {
    params: {
      bookmark: bookmarkParams,
      insightsResponse: chartDisplay.queryResults,
      disableClickNode: true
    }
  }));
}
function getRetentionCurveChart(chartDisplay, bookmarkParams) {
  return /* @__PURE__ */ jsx("div", {
    sel: ".step-chart-container",
    class: {
      "fixed-size-chart": chartDisplay.getContext(`reportLayoutManager`).hasFixedSizeChart()
    }
  }, /* @__PURE__ */ jsx(RetentionCurveChartEl, {
    sel: ".funnels-step-chart",
    attrs: {
      loaded: true,
      "enable-segment-click": true,
      "min-step-width": 266,
      "max-step-width": 600,
      "resizing-layout": chartDisplay.attr(`resizing-layout`),
      "show-popover": true
    },
    class: {
      "resizing-layout": chartDisplay.attr(`resizing-layout`)
    },
    props: {
      bookmarkData: {
        bookmark: bookmarkParams,
        queryResults: chartDisplay.queryResults,
        type: RetentionBookmarkType.MULTIMETRIC
      }
    }
  }));
}
